import { computed, signal } from '@angular/core';

// Cell editors for an @inavista/components grid row, drawn by CellEditorHost with the library's own controls
// (inavista-dropdown, inavista-textbox, inavista-date-picker). Each one keeps the members the page's rules used on the
// Syncfusion control it replaces (value, itemData, enabled, dataSource, min, max, step, decimals, format...), so those
// rules run unchanged. As in Syncfusion, setting `value` from code raises `change` too (isInteracted: false): the
// pricing and tax rules depend on it. And as in Syncfusion, it raises it LATER: a control applies what code set in
// one batch on a later task (ej2-base: setImmediate → dataBind), every new set moves its batch behind the others,
// and `change` fires once with the final value. The rules were written for that order: the cost type cascade sets
// the price (whose change computes the total) and then resets the total to 0; Syncfusion ran the price change last.

// One batch per control, run in the order the controls were last set from code.
function later(run: () => void): () => void {
  const timer = setTimeout(run);
  return () => clearTimeout(timer);
}
const NOT_PENDING = Symbol('not pending');

export interface CellEditorChange {
  value: any;
  previousValue: any;
  itemData?: any;
  isInteracted: boolean;
}

interface EditorOptions {
  value?: any;
  enabled?: boolean;
  placeholder?: string;
  change?: (event: CellEditorChange) => void;
  focus?: (event?: any) => void;
}

export abstract class CellEditor {
  abstract readonly kind: 'dropdown' | 'number' | 'text' | 'datetime' | 'checkbox';
  protected readonly current = signal<any>(null);
  readonly isEnabled = signal(true);
  readonly placeholder: string;
  protected readonly onChange?: (event: CellEditorChange) => void;
  readonly onFocus?: (event?: any) => void;
  private destroyed = false;

  constructor(options: EditorOptions) {
    this.current.set(this.normalize(options.value ?? null));
    this.isEnabled.set(options.enabled ?? true);
    this.placeholder = options.placeholder ?? '';
    this.onChange = options.change;
    this.onFocus = options.focus;
  }

  get value() { return this.current(); }
  set value(value: any) { this.setValue(value, false); }

  get enabled() { return this.isEnabled(); }
  set enabled(enabled: boolean) { this.isEnabled.set(!!enabled); this.touched(); }

  get itemData(): any { return undefined; }

  // The value before the first set from code of the batch still waiting, and that batch's cancel.
  private pendingPrevious: any = NOT_PENDING;
  private cancelPending: (() => void) | null = null;

  /** The value the control shows; `byUser` when the user typed or picked it. */
  setValue(value: any, byUser: boolean) {
    const next = this.normalize(value);
    const previousValue = this.current();
    if (!byUser) {
      // Read back at once, as Syncfusion's getter; `change` waits for the batch.
      if (this.pendingPrevious === NOT_PENDING) this.pendingPrevious = previousValue;
      this.current.set(next);
      this.touched();
      return;
    }
    if (this.same(previousValue, next)) return;
    this.current.set(next);
    if (!this.destroyed) this.onChange?.({ value: next, previousValue, itemData: this.itemData, isInteracted: true });
  }

  /** A property set from code: a waiting batch moves behind the others (a min or max change may clamp the value). */
  protected touched(clamps = false) {
    if (clamps && this.pendingPrevious === NOT_PENDING) this.pendingPrevious = this.current();
    if (this.pendingPrevious === NOT_PENDING) return;
    this.cancelPending?.();
    this.cancelPending = later(() => this.applyPending());
  }

  private applyPending() {
    const previousValue = this.pendingPrevious;
    this.pendingPrevious = NOT_PENDING;
    this.cancelPending = null;
    const value = this.normalize(this.current());
    this.current.set(value);
    if (!this.destroyed && !this.same(previousValue, value)) {
      this.onChange?.({ value, previousValue, itemData: this.itemData, isInteracted: false });
    }
  }

  /** The cell closed: later sets from the page's rules no longer raise `change`, as on a destroyed Syncfusion control. */
  destroy() {
    this.destroyed = true;
    this.cancelPending?.();
  }

  protected normalize(value: any) { return value; }
  protected same(a: any, b: any) { return a === b; }
}

export class DropdownEditor extends CellEditor {
  readonly kind = 'dropdown';
  private readonly items = signal<any[]>([]);
  private readonly valueField: string;
  private readonly textField: string;
  readonly allowFiltering: boolean;
  /** Typing in the list's search box asks the page for rows (the old `filtering`), the page sets `dataSource`. */
  readonly onFiltering?: (event: { text: string }) => void;
  /** With `filtering`: the list still filters what it shows (Syncfusion's default), the page only reloads it. */
  readonly filterLocally: boolean;
  readonly popupWidth: string | null;
  readonly clearable: boolean;
  readonly openOnFocus: boolean;
  readonly loading = signal(false);
  /** An icon button beside the list (the Item column's Advanced Search). */
  readonly action?: { title: string; iconClass: string; click: () => void };
  readonly actionVisible = signal(false);
  private openPanel: (() => void) | null = null;
  private focusControl: (() => void) | null = null;

  constructor(options: EditorOptions & {
    dataSource?: any[];
    fields?: { value: string; text: string };
    allowFiltering?: boolean;
    filtering?: (event: { text: string }) => void;
    filterLocally?: boolean;
    popupWidth?: string;
    showClearButton?: boolean;
    openOnFocus?: boolean;
    action?: { title: string; iconClass: string; click: () => void };
  }) {
    super(options);
    this.valueField = options.fields?.value ?? 'value';
    this.textField = options.fields?.text ?? 'text';
    this.items.set(options.dataSource ?? []);
    this.allowFiltering = !!options.allowFiltering || !!options.filtering;
    this.onFiltering = options.filtering;
    this.filterLocally = !!options.filterLocally;
    this.popupWidth = options.popupWidth ?? null;
    this.clearable = !!options.showClearButton;
    this.openOnFocus = !!options.openOnFocus;
    this.action = options.action;
  }

  get dataSource() { return this.items(); }
  // New rows end the search spinner, as Syncfusion's list did once it rendered them (some pages never hide it).
  set dataSource(rows: any[]) { this.items.set(rows ?? []); this.loading.set(false); this.touched(); }

  /** The row of `dataSource` the value stands for. */
  get itemData() {
    const value = this.current();
    if (value === null || value === undefined) return null;
    return this.items().find(row => String(row?.[this.valueField]) === String(value)) ?? null;
  }

  // Built once per dataSource: a new array on every check would read as new rows to the dropdown, which then drops
  // its keyboard highlight (arrows and Enter would do nothing).
  readonly options = computed(() => this.items().map(row => {
    const label = String(row?.[this.textField] ?? '');
    return { value: row?.[this.valueField], label, tooltip: label };
  }));

  /** What the closed list shows: the picked row's text, or the stored code while its row isn't loaded yet. */
  readonly selectedLabel = () => {
    const value = this.current();
    if (value === null || value === undefined || value === '') return null;
    return this.itemData?.[this.textField] ?? String(value);
  };

  showPopup() { this.openPanel?.(); }
  focusIn() { this.focusControl?.(); }
  showSpinner() { this.loading.set(true); }
  hideSpinner() { this.loading.set(false); }

  /** CellEditorHost hands the drawn control's openPanel and focus here. */
  attachPanel(open: (() => void) | null, focus: (() => void) | null = null) { this.openPanel = open; this.focusControl = focus; }
}

export class NumberEditor extends CellEditor {
  readonly kind = 'number';
  private readonly minValue = signal<number | null>(null);
  private readonly maxValue = signal<number | null>(null);
  readonly stepValue = signal<number | null>(1);
  private readonly decimalPlaces = signal<number | null>(null);
  private readonly formatText = signal<string>('n2');
  /** Text shown on hover (the Quantity column's On Hand). */
  readonly hint?: () => string | null;
  /** Typing is applied after this pause (ms); 0 applies each key, as the old keyup handlers did. */
  readonly typingDelay: number;

  constructor(options: EditorOptions & {
    min?: number | null; max?: number | null; step?: number; decimals?: number | null; format?: string;
    hint?: () => string | null; typingDelay?: number;
  }) {
    super({ ...options, value: null });
    this.minValue.set(options.min ?? null);
    this.maxValue.set(options.max ?? null);
    this.stepValue.set(options.step ?? 1);
    this.decimalPlaces.set(options.decimals ?? null);
    this.formatText.set(options.format ?? 'n2');
    this.hint = options.hint;
    this.typingDelay = options.typingDelay ?? 0;
    this.current.set(this.normalize(options.value ?? null));
  }

  get min() { return this.minValue(); }
  set min(min: number | null) { this.minValue.set(min ?? null); this.touched(true); }
  get max() { return this.maxValue(); }
  set max(max: number | null) { this.maxValue.set(max ?? null); this.touched(true); }
  get step() { return this.stepValue(); }
  set step(step: number) { this.stepValue.set(step); this.touched(); }
  get decimals() { return this.decimalPlaces(); }
  set decimals(decimals: number | null) { this.decimalPlaces.set(decimals ?? null); this.touched(true); }
  get format() { return this.formatText(); }
  set format(format: string) { this.formatText.set(format); this.touched(); }

  /** The decimals shown: `decimals`, else the format's (n2 by default, as Syncfusion). */
  readonly shownDecimals = () => this.decimalPlaces() ?? (+(this.formatText()?.match(/\d+$/)?.[0] ?? 0));

  // As Syncfusion's strict mode: a number, rounded to `decimals` when set, kept within min and max.
  protected override normalize(value: any) {
    if (value === null || value === undefined || value === '') return null;
    let n = typeof value === 'number' ? value : Number(String(value).replace(/,/g, ''));
    if (Number.isNaN(n)) return null;
    const decimals = this.decimalPlaces?.();
    if (decimals !== null && decimals !== undefined) n = +n.toFixed(decimals);
    const min = this.minValue?.(), max = this.maxValue?.();
    if (min !== null && min !== undefined && n < min) n = min;
    if (max !== null && max !== undefined && n > max) n = max;
    return n;
  }
}

export class TextEditor extends CellEditor {
  readonly kind = 'text';
  protected override normalize(value: any) { return value === undefined ? null : value; }
}

/** A tick box; `checked` as on the Syncfusion CheckBox. */
export class CheckboxEditor extends CellEditor {
  readonly kind = 'checkbox';
  get checked() { return this.value; }
  set checked(checked: boolean) { this.value = checked; }
  protected override normalize(value: any) { return !!value; }
}

export class DateTimeEditor extends CellEditor {
  readonly kind = 'datetime';
  private readonly minValue = signal<Date | null>(null);
  private readonly maxValue = signal<Date | null>(null);
  readonly allowEdit: boolean;
  readonly openOnFocus: boolean;

  constructor(options: EditorOptions & { min?: Date | string | null; max?: Date | string | null; allowEdit?: boolean; openOnFocus?: boolean }) {
    super(options);
    this.minValue.set(toDate(options.min));
    this.maxValue.set(toDate(options.max));
    this.allowEdit = options.allowEdit ?? true;
    this.openOnFocus = !!options.openOnFocus;
  }

  get min() { return this.minValue(); }
  set min(min: Date | string | null) { this.minValue.set(toDate(min)); this.touched(); }
  get max() { return this.maxValue(); }
  set max(max: Date | string | null) { this.maxValue.set(toDate(max)); this.touched(); }

  protected override normalize(value: any) { return toDate(value); }
  protected override same(a: Date | null, b: Date | null) { return (a?.getTime() ?? null) === (b?.getTime() ?? null); }
}

function toDate(value: any): Date | null {
  if (value === null || value === undefined || value === '') return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

/** A column's cell editor: `open` builds the control when the cell is drawn, `read` gives its value on save. */
export interface CellEdit {
  open: (args: { rowData: any; column: { field: string } }) => CellEditor;
  read: () => any;
  destroy?: () => void;
}
